import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import TextArea from 'ui-library/bs/TextArea';
import {GRAY_DARK, RED_LIGHT, RED} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useOnMount from 'ui-library/hooks/useOnMount';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, TextAreaChangeEvent} from 'util/Types';

import {
  DataModificationsHeader,
  encodeReasonCodeAndCommentHeader,
  ReasonCodeAndCommentType,
} from './DataModifications';
import {
  DataModificationsAnalayticsParams,
  trackModalClose,
  trackModalOpen,
  trackModalSubmit,
} from './DataModificationsTracking';

const MAX_COMMENT_LENGTH = 255;

export interface ReasonCodeAndCommentModalProps {
  changeSummary: string | JSX.Element;
  onRequestClose: () => void;
  onRequestSubmit: (dataModsHeader?: DataModificationsHeader) => Promise<void> | void;
  analyticsParams?: DataModificationsAnalayticsParams;
  // need to know both the unique key for this modal and the key prefix to close any previous modals to
  // handle an edge-case where a second modal needs to be shown, overriding the first
  modalKey: string;
  modalKeyPrefix: string;
  modalTitle: string;
  reasonCodes: () => Array<OptionData>; // is a function to get order of test config mock loading correct
  isCommentOptional?: boolean;
  dataTestAttrs?: DataTestAttrs;
  reasonCodeAndCommentType: ReasonCodeAndCommentType;
  /**
   * Optional reason code and comments to include from other modals that may have been shown before data mods
   */
  additionalReasonCodeAndComments?: DataModificationsHeader;
}

export function ChangeSummary(props: {
  currentChangeSummary: string | JSX.Element;
  previousChangeSummary: string | JSX.Element | null;
}): JSX.Element {
  function maybeTextToElement(textOrElement: string | JSX.Element): JSX.Element {
    return typeof textOrElement === 'string' ? <p>{textOrElement}</p> : textOrElement;
  }

  if (props.previousChangeSummary != null) {
    return (
      <div className={css(styles.changeSummary)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={
            <>
              <strong>Previous change:</strong>
              {maybeTextToElement(props.previousChangeSummary)}
              <strong>Additional change:</strong>
              {maybeTextToElement(props.currentChangeSummary)}
            </>
          }
        />
      </div>
    );
  } else if (props.currentChangeSummary) {
    return (
      <div className={css(styles.changeSummary)}>
        {typeof props.currentChangeSummary === 'string' ? (
          <InlineBanner bannerType={InlineBannerType.INFO} text={props.currentChangeSummary} />
        ) : (
          props.currentChangeSummary
        )}
      </div>
    );
  } else {
    return <></>;
  }
}

function _closePreviousModalAndSaveChangeSummary(
  currentModalKey: string,
  modalKeyPrefix: string
): string | JSX.Element | null {
  let previousChangeSummary: string | JSX.Element | null = null;

  const dataModsModals = ModalContainer.getModalsByKeyPrefixString(modalKeyPrefix);
  const previousDataModsModal = dataModsModals?.find(
    (modal: React.Component) => (modal.props as ReasonCodeAndCommentModalProps).modalKey !== currentModalKey
  );
  if (previousDataModsModal) {
    const previousProps = (previousDataModsModal as React.Component).props as ReasonCodeAndCommentModalProps;
    ModalContainer.close(previousProps.modalKey);
    previousChangeSummary = previousProps.changeSummary;
  }
  return previousChangeSummary;
}

/**
 * Generic modal for providing a reason code and comment
 */
export default function ReasonCodeAndCommentModal(props: ReasonCodeAndCommentModalProps): JSX.Element {
  useOnMount(() => {
    if (props.analyticsParams) {
      trackModalOpen(props.analyticsParams);
    }
  });

  const [previousChangeSummary, setPreviousChangeSummary] = useState(null as string | JSX.Element | null);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonComment, setReasonComment] = useState('');
  const [hasLengthError, setHasLengthError] = useState(false);
  const isSubmitEnabled = Boolean(
    reasonCode && ((reasonComment && !hasLengthError) || (!reasonComment && props.isCommentOptional))
  );
  const dataTestAttrsComponent = props.dataTestAttrs?.component ?? 'ReasonCodeAndCommentModal';

  useEffect(() => {
    // Handle case where second modal needs to be shown, overriding the first
    setPreviousChangeSummary(_closePreviousModalAndSaveChangeSummary(props.modalKey, props.modalKeyPrefix));
  }, [props.modalKey, props.modalKeyPrefix]);

  const _handleChangeComment = (e: TextAreaChangeEvent): void => {
    const reasonComment = e.target.value;
    setHasLengthError(reasonComment.length > MAX_COMMENT_LENGTH);
    setReasonComment(reasonComment);
  };

  function _handleEnterPress(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
    }
  }

  const _handleOnRequestClose = (): void => {
    if (props.analyticsParams) {
      trackModalClose(props.analyticsParams);
    }
    props.onRequestClose();
  };

  const _handleOnRequestSubmit = async (): Promise<DataModificationsHeader> => {
    if (props.analyticsParams) {
      trackModalSubmit(props.analyticsParams, reasonCode);
    }
    const dataModsHeader = encodeReasonCodeAndCommentHeader(
      props.reasonCodeAndCommentType,
      reasonCode,
      reasonComment,
      props.additionalReasonCodeAndComments
    );
    await props.onRequestSubmit(dataModsHeader);
    return dataModsHeader;
  };

  return (
    <Modal
      onRequestClose={_handleOnRequestClose}
      onRequestSubmit={_handleOnRequestSubmit}
      title={props.modalTitle}
      medium={true}
      buttons={[
        {
          text: 'Submit',
          submit: true,
          disabled: !isSubmitEnabled,
        },
      ]}
      dataTestAttrs={{component: dataTestAttrsComponent, element: 'root'}}
    >
      <Row>
        <Col md={12}>
          <ChangeSummary
            currentChangeSummary={props.changeSummary}
            previousChangeSummary={previousChangeSummary}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className={css(styles.reasonDropdownContainer)}>
            <Label>Reason</Label>
            <SelectDropdown
              displayName={reasonCode || 'Choose reason...'}
              menu={
                <SearchableMenu
                  onSelect={setReasonCode}
                  selectedValue={reasonCode}
                  width={Menu.WIDTHS.FIT_CONTENT}
                  height={Menu.HEIGHTS.MEDIUM}
                  scrollable={true}
                  optionDatas={props.reasonCodes()}
                />
              }
              dataTestAttrs={{component: dataTestAttrsComponent, element: 'form', key: 'reason'}}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Label>Additional note {props.isCommentOptional ? '(optional)' : ''}</Label>
          <TextArea
            name="reasonComment"
            value={reasonComment}
            onChange={_handleChangeComment}
            onKeyPress={_handleEnterPress}
            rows={4}
            className={css(hasLengthError && styles.errorTextArea)}
          />
          <p className={css(hasLengthError ? styles.errorCounter : styles.counter)}>
            {reasonComment.length}/{MAX_COMMENT_LENGTH} characters
          </p>
        </Col>
      </Row>
    </Modal>
  );
}
const styles = StyleSheet.create({
  errorCounter: {
    marginTop: '5px',
    textAlign: 'right',
    color: RED_LIGHT,
  },
  errorTextArea: {
    border: '1px solid',
    borderColor: RED,
  },
  counter: {
    marginTop: '5px',
    textAlign: 'right',
    color: GRAY_DARK,
  },
  changeSummary: {
    whiteSpace: 'pre-line',
    marginBottom: '10px',
  },
  reasonDropdownContainer: {
    marginBottom: '10px',
  },
});
